import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';
import type {
  CreateFlowInstanceRequest,
  FlowInstance,
  FlowInstanceStepItem,
  OnboardingBranding,
} from './types';

/**
 * `item_config` is a jsonb column the Tenant API has historically stored as a
 * JSON *string* (double-encoded). The backend parses it defensively everywhere;
 * normalise it here too so the runner reads a plain object (input_type,
 * validation_rules, is_required, …) instead of a string it can't render.
 */
function parseItemConfig(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  }
  return value as Record<string, unknown> | undefined;
}

function normalizeItem(item: FlowInstanceStepItem): FlowInstanceStepItem {
  return {
    ...item,
    item_config: parseItemConfig(item.item_config),
    children: item.children?.map(normalizeItem),
  };
}

function normalizeInstance(instance: FlowInstance): FlowInstance {
  if (!instance?.steps) return instance;
  return {
    ...instance,
    steps: instance.steps.map((step) => ({
      ...step,
      items: step.items?.map(normalizeItem),
    })),
  };
}

/** Where a flow instance's access token is kept, per instance, for the life of this session. */
const ACCESS_TOKEN_KEY_PREFIX = 'flow_instance_token:';

/** Which instance each onboarding opened in this session, so resuming it can carry that instance's token. */
const ONBOARDING_INSTANCE_KEY_PREFIX = 'flow_onboarding_instance:';

/**
 * Keep the access token the API hands out with an instance (on start, resume, a join, an access
 * invite or a survey). The public flow routes open an instance only to that token or to the
 * signed-in member it belongs to, and resuming an onboarding by its id asks for the same.
 */
export function rememberFlowAccessToken(
  instanceId?: string | null,
  token?: string | null,
  onboardingId?: string | null,
): void {
  if (instanceId && token) sessionStorage.setItem(ACCESS_TOKEN_KEY_PREFIX + instanceId, token);
  if (instanceId && onboardingId) sessionStorage.setItem(ONBOARDING_INSTANCE_KEY_PREFIX + onboardingId, instanceId);
}

function accessHeaders(instanceId: string): Record<string, string> {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY_PREFIX + instanceId);
  return token ? { 'X-Flow-Instance-Token': token } : {};
}

/**
 * Onboarding API — canonical `/public/flow-instances/*` routes, mirroring
 * the Angular member-portal `OnboardingService`. Onboarding is a stateful
 * server flow: create an instance, value its items, complete steps, advance.
 */

export async function createFlowInstance(req: CreateFlowInstanceRequest): Promise<FlowInstance> {
  // An onboarding this session already opened is resumed, which only its token (or its member) may do.
  const opened = req.onboarding_id
    ? sessionStorage.getItem(ONBOARDING_INSTANCE_KEY_PREFIX + req.onboarding_id)
    : null;
  const { data } = await tenantApi.post('/public/flow-instances', req, {
    headers: opened ? accessHeaders(opened) : {},
  });
  const instance = unwrap<FlowInstance>(data);
  rememberFlowAccessToken(instance.instance_id, instance.access_token, req.onboarding_id);
  return normalizeInstance(instance);
}

export async function getFlowInstance(instanceId: string): Promise<FlowInstance> {
  const { data } = await tenantApi.get(`/public/flow-instances/${instanceId}`, { headers: accessHeaders(instanceId) });
  return normalizeInstance(unwrap<FlowInstance>(data));
}

export async function getOnboardingBranding(onboardingId: string): Promise<OnboardingBranding> {
  const { data } = await tenantApi.get(`/public/onboarding/${onboardingId}/branding`);
  return unwrap<OnboardingBranding>(data);
}

export async function updateItemValue(
  instanceId: string,
  itemId: string,
  value: unknown,
): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/items/${itemId}/value`, { value }, { headers: accessHeaders(instanceId) });
}

export async function executeItemAction(
  instanceId: string,
  itemId: string,
  parameters: Record<string, unknown> = {},
): Promise<void> {
  await tenantApi.post(
    `/public/flow-instances/${instanceId}/items/${itemId}/execute`,
    { parameters },
    { headers: accessHeaders(instanceId) },
  );
}

export async function completeStep(instanceId: string, stepId: string): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/steps/${stepId}/complete`, undefined, { headers: accessHeaders(instanceId) });
}

/** Opens another pass on a repeating section. */
export async function addRepeatPass(instanceId: string, sectionItemId: string): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/items/${sectionItemId}/repeat`, undefined, { headers: accessHeaders(instanceId) });
}

/** Removes one pass from a repeating section. */
export async function removeRepeatPass(
  instanceId: string,
  sectionItemId: string,
  passIndex: number,
): Promise<void> {
  await tenantApi.delete(`/public/flow-instances/${instanceId}/items/${sectionItemId}/repeat/${passIndex}`, {
    headers: accessHeaders(instanceId),
  });
}

export async function activateStep(instanceId: string, stepId: string): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/steps/${stepId}/activate`, undefined, { headers: accessHeaders(instanceId) });
}

export async function moveToNextStep(instanceId: string): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/next-step`, undefined, { headers: accessHeaders(instanceId) });
}

export async function moveToPreviousStep(instanceId: string): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/previous-step`, undefined, { headers: accessHeaders(instanceId) });
}

export async function completeInstance(instanceId: string): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/complete`, undefined, { headers: accessHeaders(instanceId) });
}

/** Request an OTP for an `otpverify` action item. Returns the verify token. */
export async function requestFlowOtp(
  instanceId: string,
  itemId: string,
  destination?: string,
): Promise<{ token?: string; [key: string]: unknown }> {
  const { data } = await tenantApi.post(
    `/public/flow-instances/${instanceId}/items/${itemId}/otp/request`,
    { destination },
    { headers: accessHeaders(instanceId) },
  );
  return unwrap<{ token?: string; [key: string]: unknown }>(data);
}

/** Verify the OTP code for an `otpverify` action item. */
export async function verifyFlowOtp(
  instanceId: string,
  itemId: string,
  token: string,
  code: string,
): Promise<void> {
  await tenantApi.post(
    `/public/flow-instances/${instanceId}/items/${itemId}/otp/verify`,
    { token, code },
    { headers: accessHeaders(instanceId) },
  );
}

export async function uploadFlowFiles(instanceId: string, files: File[]): Promise<string[]> {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  const { data } = await tenantApi.post(`/public/flow-instances/${instanceId}/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data', ...accessHeaders(instanceId) },
  });
  const result = unwrap<{ files?: Array<{ url: string }> }>(data);
  return (result.files ?? []).map((f) => f.url);
}
